import { PanelRightClose, PanelRightOpen, X } from 'lucide-react';
import { useRecoilValue } from 'recoil';

import { Button } from '@/components/button';
import { Checkbox } from '@/components/inputs/check-box';
import { ComboboxMulti } from '@/components/ui/inputs/combobox-multi';
import { useScheduleFilters } from '@/hooks/use-schedule-filters';
import {
  FILTER_BODYTYPE,
  FILTER_CARRIER_IDS,
  FILTER_DIRECT_SERVICES_ONLY,
  FILTER_HAULING_CARGO_AIRCRAFT_ONLY,
  FILTER_HIDE_RFS,
  MODE_AIR,
} from '@/lib/constants';
import { getCarriersByIdsState } from '@/state/carriers';
import { scheduleFiltersActiveCountState, scheduleFiltersState } from '@/state/standalone-schedules';
import { FilterSelection, RouteScheduleInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { tv } from '@/utils/styles';

import { getCarrierOptions, getScheduleCarriers } from './utils';

export interface SchedulesFilterBarProps {
  schedules: RouteScheduleInterface[];
  className?: string;
  mode: SegmentModeType;
  isSidebarClosed?: boolean;
  onToggleSidebar?: () => void;
}

const SchedulesFilterBar = ({
  schedules = [],
  className,
  mode,
  isSidebarClosed,
  onToggleSidebar,
}: SchedulesFilterBarProps) => {
  const isAir = mode === MODE_AIR;

  const carriers = getScheduleCarriers(schedules);
  const scheduleFiltersActiveCount = useRecoilValue(scheduleFiltersActiveCountState);
  const carriersFromIds = useRecoilValue(getCarriersByIdsState(carriers));
  const scheduleFilters = useRecoilValue(scheduleFiltersState);

  const { updateFilters, resetAllFilters } = useScheduleFilters();

  const carrierOptions = getCarrierOptions(carriersFromIds);

  const handleSelectChange = (key: keyof FilterSelection, values: string[]) => {
    updateFilters(key, values);
  };

  const handleCheckboxChange = (key: keyof FilterSelection) => {
    const isSelected = !scheduleFilters[key];
    updateFilters(key, isSelected);
  };

  const { base, filtersWrapper, divider, checkboxHit, checkboxStyles, clearButton } = styles();

  return (
    <div className={base({ className })}>
      <div className={filtersWrapper()}>
        <ComboboxMulti
          options={[{ title: 'Options', items: carrierOptions }]}
          placeholder="Filter by carrier"
          onValueChange={(vals: string[]) => handleSelectChange(FILTER_CARRIER_IDS, vals)}
          selectedValues={scheduleFilters[FILTER_CARRIER_IDS] || []}
          dropdownMessage="Only carriers with active schedules are listed"
        />
        <Checkbox
          value="true"
          id={FILTER_DIRECT_SERVICES_ONLY}
          onChange={() => handleCheckboxChange(FILTER_DIRECT_SERVICES_ONLY)}
          isSelected={scheduleFilters[FILTER_DIRECT_SERVICES_ONLY] || false}
          className={checkboxStyles({ isChecked: !!scheduleFilters[FILTER_DIRECT_SERVICES_ONLY] })}
        >
          Direct services only
          <span className={checkboxHit()} />
        </Checkbox>
        {isAir && (
          <>
            <div className={divider()} />
            <ComboboxMulti
              options={[
                {
                  title: 'Options',
                  items: [
                    { value: 'widebody', label: 'Widebody' },
                    { value: 'narrowbody', label: 'Narrowbody' },
                  ],
                },
              ]}
              placeholder="Filter by fuselage"
              onValueChange={(vals: string[]) => handleSelectChange(FILTER_BODYTYPE, vals)}
              selectedValues={scheduleFilters[FILTER_BODYTYPE] || []}
            />
            <Checkbox
              value="true"
              id={FILTER_HAULING_CARGO_AIRCRAFT_ONLY}
              onChange={() => handleCheckboxChange(FILTER_HAULING_CARGO_AIRCRAFT_ONLY)}
              isSelected={scheduleFilters[FILTER_HAULING_CARGO_AIRCRAFT_ONLY] || false}
              className={checkboxStyles({ isChecked: !!scheduleFilters[FILTER_HAULING_CARGO_AIRCRAFT_ONLY] })}
            >
              Dedicated freighter only
              <span className={checkboxHit()} />
            </Checkbox>
            <Checkbox
              value="true"
              id={FILTER_HIDE_RFS}
              onChange={() => handleCheckboxChange(FILTER_HIDE_RFS)}
              isSelected={scheduleFilters[FILTER_HIDE_RFS] || false}
              className={checkboxStyles({ isChecked: !!scheduleFilters[FILTER_HIDE_RFS] })}
            >
              Hide Road Feeder Services
              <span className={checkboxHit()} />
            </Checkbox>
          </>
        )}
        {scheduleFiltersActiveCount > 0 && (
          <div>
            <button type="button" onClick={resetAllFilters} className={clearButton()}>
              <X className="size-4" />
              Clear filters
            </button>
          </div>
        )}
      </div>
      <Button
        onClick={onToggleSidebar}
        size="small"
        variant="outline"
        className="ml-auto hidden h-8 lg:flex"
        iconLeft={isSidebarClosed ? <PanelRightOpen size={16} /> : <PanelRightClose size={16} />}
      >
        {isSidebarClosed ? 'Show' : 'Hide'} sidebar
      </Button>
    </div>
  );
};

const styles = tv({
  slots: {
    base: '-mx-2 mb-4 flex max-w-full items-center justify-between overflow-x-auto pl-4 pr-0 md:mb-2',
    filtersWrapper: 'flex min-w-[--app-content-max-width] flex-wrap items-center gap-2 lg:min-w-none',
    divider: 'mx-2 h-7 border-l border-grey-300',
    checkboxStyles:
      'relative h-[--input-height-small] rounded-md border border-dashed border-grey-350 bg-grey-100 px-2 py-1.5 text-baseSm text-grey-800 hover:border-grey-600 hover:text-text-primary',
    checkboxHit: 'absolute left-0 top-0 h-full w-full rounded-md',
    clearButton:
      'flex min-h-[--input-height-small] items-center gap-1 rounded-md p-2 text-baseSm transition-all duration-150 hover:bg-grey-200',
  },
  variants: {
    isChecked: {
      true: {
        checkboxStyles: 'border-solid border-grey-700 bg-white text-text-primary',
      },
      false: null,
    },
  },
});

export { SchedulesFilterBar };
